import {
  isInfrastructureXmlErrorBody,
  UPSTREAM_UNAVAILABLE_MESSAGE,
} from './utils/upstreamHttpResponse';

/** True when user-visible copy must not be shown (GCS/XML infra, billing errors, etc.). */
export function isXmlLikeInfrastructureText(raw: string): boolean {
  const t = raw.trim();
  if (!t) return false;
  if (isInfrastructureXmlErrorBody(t)) return true;
  if (t.startsWith('<?xml') || t.startsWith('<')) return true;
  return /UserProjectAccountProblem|<Error>\s*<Code>/i.test(t);
}

/** Strip XML / infra payloads from API error strings before showing in the UI. */
export function sanitizeUserFacingMessage(
  raw: string,
  fallback = UPSTREAM_UNAVAILABLE_MESSAGE
): string {
  if (!raw.trim()) return fallback;
  if (isXmlLikeInfrastructureText(raw)) return fallback;
  return raw;
}

export function staticSiteDownPath(): string {
  const base = import.meta.env.BASE_URL || '/';
  return `${base}service-unavailable.html`.replace(/([^:]\/)\/+/g, '$1');
}

export function navigateToStaticSiteDown(): void {
  if (typeof window === 'undefined') return;
  const target = staticSiteDownPath();
  if (window.location.pathname.endsWith('service-unavailable.html')) return;
  window.location.replace(target);
}

function pageBodyLooksLikeXmlError(): boolean {
  if (typeof document === 'undefined') return false;
  const root = document.documentElement;
  if (!root || root.nodeName === 'Error') return true;
  const text = document.body?.innerText ?? '';
  return isXmlLikeInfrastructureText(text);
}

/** Early guards: redirect before React mounts when the shell looks like GCS/XML output. */
export function installSiteDownGuards(): void {
  if (typeof window === 'undefined') return;

  if (pageBodyLooksLikeXmlError()) {
    navigateToStaticSiteDown();
    return;
  }

  const onGlobalFailure = (event: ErrorEvent | PromiseRejectionEvent) => {
    const message =
      event instanceof ErrorEvent
        ? event.message
        : event.reason instanceof Error
          ? event.reason.message
          : String(event.reason ?? '');
    if (isXmlLikeInfrastructureText(message)) {
      event.preventDefault?.();
      navigateToStaticSiteDown();
    }
  };

  window.addEventListener('error', onGlobalFailure);
  window.addEventListener('unhandledrejection', onGlobalFailure);
}
